import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { listExchangeRuns, type ExchangeRunListRow } from "@/lib/exchange-trace.functions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { FilterBar } from "@/components/ui/filter-bar";
import { PageHeader } from "@/components/ui/page-header";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";
import { ArrowRight, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const searchSchema = dateRangeSearchSchema.extend({
  q: z.string().optional(),
  entity: z.string().optional(),
  source: z.string().optional(),
  outcome: z.enum(["ok", "failed", "quarantined", ""]).optional(),
  mode: z.enum(["commit", "dry_run", ""]).optional(),
});

export const Route = createFileRoute("/_authenticated/app/admin/exchange-runs")({
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys, "q", "entity", "source", "outcome", "mode"])] },
  component: ExchangeRunsList,
});

function ExchangeRunsList() {
  const list = useServerFn(listExchangeRuns);
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });

  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["exchange-runs", urlSearch, fromIso, toIso],
    queryFn: () =>
      list({
        data: {
          search: urlSearch.q?.trim() || undefined,
          entity: urlSearch.entity || undefined,
          source_kind: urlSearch.source || undefined,
          outcome: urlSearch.outcome || undefined,
          mode: urlSearch.mode || undefined,
          from_iso: fromIso,
          to_iso: toIso,
          limit: 200,
        },
      }),
    refetchInterval: 10_000,
  });

  const cols: ColumnDef<ExchangeRunListRow, unknown>[] = [
    {
      id: "outcome",
      header: "",
      enableSorting: false,
      cell: ({ row }) => <OutcomeIcon row={row.original} />,
    },
    {
      id: "completed_at",
      header: "When",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {row.original.completed_at
            ? new Date(row.original.completed_at).toLocaleString()
            : new Date(row.original.started_at).toLocaleString()}
        </span>
      ),
    },
    {
      id: "entity",
      header: "Entity",
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.entity}</span>,
    },
    {
      id: "source",
      header: "Source",
      cell: ({ row }) => (
        <div className="text-sm">
          <div className="font-medium">{row.original.source_kind}</div>
          {row.original.source_namespace && (
            <div className="text-xs text-muted-foreground">{row.original.source_namespace}</div>
          )}
        </div>
      ),
    },
    {
      id: "source_record_id",
      header: "External ID",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.source_record_id ?? "—"}</span>
      ),
    },
    {
      id: "mode",
      header: "Mode",
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">{row.original.mode}</Badge>
      ),
    },
    {
      id: "persisted_id",
      header: "Persisted",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.persisted_id ?? "—"}
        </span>
      ),
    },
    {
      id: "signals",
      header: "Signals",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-1.5 text-xs">
          {row.original.error_count > 0 && (
            <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
              {row.original.error_count} err
            </Badge>
          )}
          {row.original.unresolved_count > 0 && (
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
              {row.original.unresolved_count} quarantine
            </Badge>
          )}
          {row.original.warning_count > 0 && (
            <Badge variant="outline" className="bg-muted text-muted-foreground">
              {row.original.warning_count} warn
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: "open",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <Link
          to="/app/admin/exchange-runs/$runId"
          params={{ runId: row.original.id }}
          className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
        >
          Open <ArrowRight className="h-3 w-3 ml-1" />
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Exchange Traces"
        description={
          "Per-envelope pipeline traces. Click a row to see how an external payload became " +
          "(or failed to become) a CairnOS object. Read-only."
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <FilterBar
          search={urlSearch.q ?? ""}
          onSearchChange={(v) => update({ q: v || undefined })}
          searchPlaceholder="Search envelope id, external id, persisted id…"
        >
          <Select
            value={urlSearch.entity ?? "all"}
            onValueChange={(v) => update({ entity: v === "all" ? undefined : v })}
          >
            <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="All entities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="order">order</SelectItem>
              <SelectItem value="sku">sku</SelectItem>
              <SelectItem value="customer">customer</SelectItem>
              <SelectItem value="inventory_adjustment">inventory_adjustment</SelectItem>
              <SelectItem value="asn">asn</SelectItem>
              <SelectItem value="tracking_update">tracking_update</SelectItem>
              <SelectItem value="location">location</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={urlSearch.source ?? "all"}
            onValueChange={(v) => update({ source: v === "all" ? undefined : v })}
          >
            <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="All sources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="rest">rest</SelectItem>
              <SelectItem value="csv">csv</SelectItem>
              <SelectItem value="shopify">shopify</SelectItem>
              <SelectItem value="cartrover">cartrover</SelectItem>
              <SelectItem value="edi">edi</SelectItem>
              <SelectItem value="custom">custom</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={urlSearch.outcome ?? "all"}
            onValueChange={(v) => update({ outcome: v === "all" ? undefined : (v as any) })}
          >
            <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="All outcomes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="ok">Persisted / OK</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="quarantined">Quarantined</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={urlSearch.mode ?? "all"}
            onValueChange={(v) => update({ mode: v === "all" ? undefined : (v as any) })}
          >
            <SelectTrigger className="w-[120px] h-9"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">commit + dry_run</SelectItem>
              <SelectItem value="commit">commit</SelectItem>
              <SelectItem value="dry_run">dry_run</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => update({ start: next.start, end: next.end })}
          />
        </FilterBar>

        <DataTable
          data={(data?.runs ?? []) as ExchangeRunListRow[]}
          columns={cols}
          isLoading={isLoading}
          empty={{
            title: "No traces",
            description: "No envelopes have flowed through the Exchange pipeline for these filters.",
          }}
        />
      </div>
    </>
  );
}

function OutcomeIcon({ row }: { row: ExchangeRunListRow }) {
  if (row.unresolved_count > 0) {
    return <AlertTriangle className="h-4 w-4 text-warning" aria-label="Quarantined" />;
  }
  if (row.ok) return <CheckCircle2 className="h-4 w-4 text-success" aria-label="OK" />;
  return <XCircle className="h-4 w-4 text-destructive" aria-label="Failed" />;
}

